/**
 * Welle 14 Polish — Keyboard-Shortcuts-Hilfe.
 * Modal listet alle global registrierten Shortcuts.
 */

import { useEffect } from 'react';
import { Keyboard, X } from 'lucide-react';
import { SHORTCUTS } from '@/hooks/use-keyboard-shortcuts';

export function KeyboardShortcutsHelp({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-shortcuts-title"
    >
      <div
        className="max-w-md w-full rounded-lg border border-cockpit-border bg-cockpit-base shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="kb-shortcuts-title" className="text-sm font-semibold flex items-center gap-2 text-zinc-100">
            <Keyboard className="size-4 text-cockpit-accent" />
            Keyboard-Shortcuts
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-zinc-400 hover:text-zinc-100 hover:bg-cockpit-surface2 focus-ring"
            aria-label="Schließen"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 mb-4 leading-snug">
          Chord-Shortcuts: erst die erste Taste drücken, dann (innerhalb 1.5 s) die
          zweite. Modifier-Tasten (⌘ Cmd, Ctrl, Alt) deaktivieren die Shortcuts —
          Browser-Verhalten bleibt unverändert.
        </p>
        <ul className="space-y-1.5">
          {SHORTCUTS.map((s) => (
            <li
              key={s.chord}
              className="grid grid-cols-[7rem_1fr] items-center gap-3 text-xs"
            >
              <ShortcutChord chord={s.chord} />
              <span className="text-zinc-300">{s.description}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 pt-3 border-t border-cockpit-border text-[10px] text-zinc-500">
          In Input-Feldern sind die Shortcuts deaktiviert. ESC bricht eine
          laufende Chord-Eingabe ab.
        </p>
      </div>
    </div>
  );
}

function ShortcutChord({ chord }: { chord: string }) {
  const keys = chord.split(' ');
  return (
    <div className="flex items-center gap-1">
      {keys.map((k, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className="text-zinc-600 text-[10px]">dann</span>}
          <kbd className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 rounded border border-cockpit-border bg-cockpit-surface2 font-mono text-[11px] text-zinc-200">
            {k === '?' ? '?' : k.toUpperCase()}
          </kbd>
        </span>
      ))}
    </div>
  );
}
